import api from '@/api'
import humps from 'humps'
import getCurrentUserId from '@/utils/get-current-user-id'
const resource = 'users'

export const enumBounds = {
  incomeRange: 5,
  moneySource: 5,
  maritalStatus: 6,
  investableAssets: 6,
  investableAssetsMinValues: [50000, 100000, 150000, 400000, 750000, 1500000],
  annualSavings: 4,
  monthlyCharges: 5,
  professionnalStatus: 4,
  scp: [1000, 2100, 2200, 2300, 3100, 3200, 3600, 4100, 4600, 4700, 4800, 5100, 5400, 5500, 5600, 6100, 6600, 6900, 7000, 7100, 7200, 7300, 7400, 7500, 7600, 7700]
}

export default {
  create (credentials) {
    return api.post('/users', {user: credentials})
  },

  update (fields) {
    return api.put(`/users/${getCurrentUserId()}/profile`, {profile: fields})
  },

  changePassword (fields) {
    return api.post('/users/me/password', {credentials: fields})
  },

  requestResetPassword (fields) {
    return api.post('/users/password/reset_password', fields)
  },

  updateResetPassword ({token, password, confirmedPassword}) {
    return api.put('/users/password', {user: {
      resetPasswordToken: token,
      password: password,
      passwordConfirmation: confirmedPassword
    }})
  },

  fetch () {
    return api.get('/users/me')
  },

  fetchProfile () {
    return api.get('/users/me/profile')
  },

  getToken () {
    return api.get('/users/me/connection')
  },

  refreshToken (refreshToken) {
    return api.post('/oauth/token', {grant_type: 'refresh_token', refresh_token: refreshToken})
  },

  sendToken (token, idUser) {
    return api.post('/users/me/connection', {token, idUser})
  },

  confirm (code) {
    return api.post('/users/confirmations', {token: code})
  },

  resendConfirmation () {
    return api.get('/users/confirmations/new')
  },

  signIn (credentials) {
    return api.post('/oauth/token', {grant_type: 'password', username: credentials.email, password: credentials.password})
  },

  signOut (token) {
    return api.post('/oauth/revoke', {token: token})
  },

  deleteProfile () {
    return api.delete('/users/me')
  },
  confirmDeleteProfile (token) {
    return api.delete(`/users/${token}`)
  },
  testDeletionToken (token) {
    return api.get(`/users/${token}/deletion_validity`)
  },

  fetchPlans () {
    return api.get('/plans')
  },

  verifyCoupon (coupon, planId) {
    return api.post('/coupons/verify', { coupon, planId })
  },

  subscribe (data) {
    return api.post('/users/me/subscriptions', { subscription: data })
  },
  fetchInvitation (invitationToken) {
    return api.get(`/invitations/${invitationToken}`)
  },
  markInvitationAsRead (invitationToken) {
    return api.post(`/invitations/${invitationToken}/mark_as_read`)
  },
  fetchPaymentSources () {
    return api.get('/users/me/payment_sources')
  },
  updatePaymentSource (token) {
    return api.post('/users/me/payment_sources', {
      paymentSource: token
    })
  },
  postDefaultPaymentInfo (payload) {
    return api.post(`/${resource}/me/payment_sources/default`, payload)
  },
  fetchInvoices () {
    return api.get('/users/me/invoices')
  },

  // Real estates
  // ---------------------------------------------------------------------------
  addRealEstate (data) {
    return api.post('/users/me/real_estates', { real_estate: data })
  },

  fetchRealEstates () {
    return api.get(`/users/${getCurrentUserId()}/real_estates`)
  },

  fetchRealEstatesByUser (id) {
    return api.get(`/users/${id}/real_estates`)
  },

  fetchAmountHistoryAll (id, from) {
    const startDate = from ? ('?from=' + from) : ''
    return api.get(`/users/${id}/real_estates/series/cash_values${startDate}`)
  },

  // Accounts
  // ---------------------------------------------------------------------------
  addAccount (data) {
    return api.post('/users/me/accounts', { account: data })
  },

  // Fetch ONE specific account by id from specific user ID
  fetchAccount (id) {
    return api.get(`/users/${getCurrentUserId()}/accounts/${id}`)
  },

  // This actually means fetch ALL accounts by id of USER
  fetchAccounts (id = getCurrentUserId()) {
    return api.get(`/users/${id}/accounts`)
  },

  fetchAccountsRepartition () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/composition?type=bank&subset=wealth`)
  },

  // Aggregate
  // ---------------------------------------------------------------------------
  fetchAggregateContractRepartition (id = getCurrentUserId()) {
    return api.post(`/users/${id}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['invested_wealth', 'savings']
          }
        ],
        'aggregates': [
          {
            'id': 'accountsRepartition',
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'kind'
              }
            ]
          }
        ]
      }
    )
  },

  fetchAggregateTransactionsHistory (portfolioId, id = getCurrentUserId()) {
    return api.post(`users/${id}/transactions/aggregate`,
      {
        'filters': [
          {
            'field': 'portfolio.id',
            'match_any': [ portfolioId ]
          },
          {
            'field': 'date'
          }
        ],
        'aggregates': [
          {
            'id': 'transactionsHistory',
            'segments': [
              {
                'field': 'date',
                'clusterer': {
                  'type': 'month_interval'
                }
              },
              {
                'field': 'value',
                'clusterer': {
                  'type': 'range',
                  'ranges': [
                    { 'to': 0, 'label': 'expenses' },
                    { 'from': 0, 'label': 'incomes' }
                  ]
                }
              }
            ],
            'reducer': {
              'func': 'sum',
              'params': ['value']
            }
          }
        ]
      }
    )
  },

  fetchAggregateBankFees (portfolioId, minDate) {
    return api.post('users/me/transactions/aggregate',
      {
        'filters': [
          {
            'field': 'portfolio.id',
            'match_any': [ portfolioId ]
          },
          {
            'field': 'category.name',
            'match_any': 'Frais bancaires'
          },
          {
            'field': 'date',
            'min': minDate
          }
        ],
        'aggregates': [
          {
            'id': 'bankFees',
            'segments': [
              {
                'field': 'value',
                'clusterer': {
                  'type': 'range',
                  'ranges': [
                    { 'to': 0, 'label': 'expenses' }
                  ]
                }
              }
            ],
            'reducer': {
              'func': 'sum',
              'params': ['value']
            }
          }
        ]
      }
    )
  },

  fetchAggregateCurrentMonthExpenses (portfolioId, minDate) {
    return api.post('users/me/transactions/aggregate',
      {
        'filters': [
          {
            'field': 'portfolio.id',
            'match_any': [ portfolioId ]
          },
          {
            'field': 'date',
            'min': `${minDate}-01`
          },
          {
            'field': 'value',
            'max': 0
          }
        ],
        'aggregates': [
          {
            'id': 'currentMonthExpenses',
            'segments': [
              {
                'field': 'date',
                'clusterer': {
                  'type': 'month_interval'
                }
              },
              {
                'field': 'value',
                'clusterer': {
                  'type': 'range',
                  'ranges': [
                    { 'to': 0, 'label': 'expenses' }
                  ]
                }
              }
            ],
            'reducer': {
              'func': 'sum',
              'params': ['value']
            }
          }
        ]
      }
    )
  },

  fetchAggregateRealestateType (id = getCurrentUserId()) {
    return api.post(`/users/${id}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['real_estate']
          }
        ],
        'aggregates': [
          {
            'id': 'realestateType',
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'type'
              }
            ]
          }
        ]
      }
    )
  },

  fetchAggregateRealestateEnv (id = getCurrentUserId()) {
    return api.post(`/users/${id}/portfolios/aggregate`,
      {
        'filters': [
          {
            'field': 'category',
            'match_any': ['invested_wealth', 'savings', 'real_estate']
          }
        ],
        'aggregates': [
          {
            'id': 'realestateEnv',
            'stats': true,
            'reducer': {
              'func': 'sum',
              'params': ['amount']
            },
            'segments': [
              {
                'field': 'category',
                'clusterer': {
                  'type': 'mapper',
                  'mappings': [
                    { 'map': 'savings', 'to': 'wealth' },
                    { 'map': 'invested_wealth', 'to': 'wealth' },
                    { 'map': 'real_estate', 'to': 'real_estate' }
                  ]
                }
              }
            ]
          }
        ]
      }
    )
  },

  // Loans
  // ---------------------------------------------------------------------------
  fetchLoans () {
    return api.get(`/users/${getCurrentUserId()}/loans`)
  },

  fetchLoansHistory (subset, from) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`, { params: humps.decamelizeKeys({ portfolioSubset: subset, from }) })
  },

  // Portfolios
  // ---------------------------------------------------------------------------
  fetchPortfolios () {
    return api.get(`/users/${getCurrentUserId()}/portfolios`)
  },

  fetchPortfolioById (id) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${id}`)
  },

  fetchPortfolioHistory (id, from) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${id}/series/cash_values${from ? ('?from=' + from) : ''}`)
  },

  fetchPortfoliosHistoryAll () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`,
      {
        params: {
          portfolio_subset: 'unscoped',
          categories: ['savings', 'invested_wealth', 'real_estate']
        }
      }
    )
  },

  fetchPortfoliosHistoryWealth () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`,
      {
        params: {
          portfolio_subset: 'unscoped',
          categories: ['savings', 'invested_wealth']
        }
      }
    )
  },

  fetchAnalyses (portfolioId, section = '') {
    return api.get(`/users/${getCurrentUserId()}/portfolios/${portfolioId}/analyses/${section}`)
  },

  fetchPortfoliosByAccount (accountId) {
    return api.get(`/users/${getCurrentUserId()}/accounts/${accountId}/portfolios`)
  },

  fetchPortfolioCompleteHistory (subset = 'all') {
    return api.get(`/users/${getCurrentUserId()}/portfolios/series/cash_values`, {
      params: humps.decamelizeKeys({ portfolioSubset: subset })
    })
  },

  fetchOptimize (portfolioId, params) {
    return api.post(`/users/${getCurrentUserId()}/portfolios/${portfolioId}/optimize`, { data: params })
  },

  fetchPortfoliosRepartition () {
    return api.get(`/users/${getCurrentUserId()}/portfolios/composition?type=kind`)
  },

  fetchPortfoliosComposition (subset, type, limit = null) {
    return api.get(`/users/${getCurrentUserId()}/portfolios/composition`, { params: humps.decamelizeKeys({ portfolioSubset: subset, type, limit }) })
  },

  // Club
  // ---------------------------------------------------------------------------
  fetchClubSubscription (id = getCurrentUserId()) {
    return api.get(`/${resource}/${id}/club_subscriptions`)
  },

  fetchClubSubscriptionById (subscriptionId, id = getCurrentUserId()) {
    return api.get(`/${resource}/${id}/club_subscriptions/${subscriptionId}`)
  },

  fetchAllClubSubscription (id = getCurrentUserId()) {
    return api.get(`/${resource}/${id}/club_subscriptions?state=all`)
  },

  subscribeToClub (payload, userId = getCurrentUserId()) {
    return api.post(`/${resource}/${userId}/club_subscriptions`, payload)
  },

  updateSubscriptionStatus (payload, userId = getCurrentUserId()) {
    return api.put(`/${resource}/${userId}/club_subscriptions/${payload.id}`, payload)
  },

  approveClubSubscription (subscriptionId, payload, userId = getCurrentUserId()) {
    return api.post(`/${resource}/${userId}/club_subscriptions/${subscriptionId}/approve`, payload)
  },

  deleteClubSubscription (payload, userId = getCurrentUserId()) {
    return api.delete(`/${resource}/${userId}/club_subscriptions/${payload.id}`)
  },

  rejectClubSubscription (payload, userId = getCurrentUserId()) {
    return api.post(`/${resource}/${userId}/club_subscriptions/${payload.id}/reject`)
  },

  fetchSignUrl (subscriptionId) {
    return api.post(`/${resource}/me/club_subscriptions/${subscriptionId}/sign`)
  },

  fetchLetterDownloadLink (subscriptionId, userId = getCurrentUserId()) {
    return api.post(`/${resource}/${userId}/club_subscriptions/${subscriptionId}/download`)
  },

  postClubPaymentInfo (subscriptionId, payload, userId = getCurrentUserId()) {
    return api.post(`/${resource}/${userId}/club_subscriptions/${subscriptionId}/payment`, payload)
  }
}
